/***
 * Created on 2025/10/30
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/

import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from "react-i18next";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import {bytesToTB, normalizeSpaceSize} from "../../service/utils";
import noDataImg from '../../static/img/utils/ic-no-usage-data.svg';
import {useQuery} from "@tanstack/react-query";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useOrganization} from "../../context/OrganizationContext";
import CloudSpaceRemovedUserUsageModal from "./CloudSpaceRemovedUserUsageModal";


ChartJS.register(ArcElement, Tooltip, Legend);

const options = {
    plugins: {
        legend: {display: false},
        responsive: true,
        maintainAspectRatio: false
    },
    // cutout: "50%",
    animation: {
        duration: 300,
    },
    radius: '95%',
    hoverOffset: 10,
};

const initialData = {
    labels: [],
    datasets: [
        {
            data: [],
            backgroundColor: [
                "rgba(255, 99, 132, 0.7)",
                "rgba(54, 162, 235, 0.7)",
                "rgba(255, 206, 86, 0.7)",
                "rgba(72, 188, 132, 0.7)",
                "rgba(199, 148, 21, 0.7)",
                "rgba(21, 73, 128, 0.7)",
                "rgba(187, 205, 241, 0.7)"
            ]
        },
    ],
    unit: 'GB',     //this is for display purpose only, not for chartjs config
};

function CloudSpaceEmptyChartComponent({title}) {
    const { t } = useTranslation();

    return (
        <div className="chart-grid__column no-data">
            <div className="chart-grid__column__title">{t(title)}</div>
            <div className="chart-grid__column__no-data">
                <div className="ic-no-data">
                    <img src={noDataImg} alt=""/>
                </div>
                <div className="desc-no-data">{t('@No usage data available.@')}</div>
            </div>
        </div>
    )
}

function CloudSpaceStatsChartComponent({usage_data, state, dispatch}) {
    const { t } = useTranslation();
    const [spaceData, setSpaceData] = useState(initialData);
    const [userData, setUserData] = useState(initialData);
    const spaceChartRef = useRef();
    const userChartRef = useRef();
    const [spaceHiddenSegments, setSpaceHiddenSegments] = useState([]);
    const [userHiddenSegments, setUserHiddenSegments] = useState([]);
    const [showRemovedUserModal, setShowRemovedUserModal] = useState(false);
    const {OrganizationAPI} = useOrganizationApi();
    const {getCurrentOrg} = useOrganization();
    const org = getCurrentOrg();

    const { data } = useQuery({
        queryKey: ['user_not_in_organization_usage', state?.current_region?.region],
        queryFn: async (data) => {
            const resp = await OrganizationAPI.q2.get(`org_storage/${org.organization_id}/user?region=${state?.current_region?.region}`);
            const orgUsers = org?.users || [];
            const result = resp.result.filter(u => !u.in_org && u?.quota_usages).map((item) => {
                let user = orgUsers.find((u) => u.id === item.user_id);
                if (!user) {
                    user = org?.removed_users?.[item.user_id] || null;
                }
                return {
                    ...item,
                    status: user?.status,
                    id: item.user_id,
                    removed_at: user?.removed_at,
                    // checked: !!item?.seated_regions?.[region_data?.region]
                }
            });
            // dispatch({type: 'update_data', payload: {data: result.filter(u => u.status === 'active')}});
            return result;
        },
        enabled: state?.current_region?.top_usage?.users?.length > 0

    });

    const handleLegendClick = (index, selectedChart, chart_name) => {
        if (!selectedChart.current) return;
        const chart = selectedChart.current;

        // For doughnut or pie chart, toggle segment visibility
        const meta = chart.getDatasetMeta(0);
        if (meta?.data[index] == null) return;
        meta.data[index].hidden = !meta.data[index].hidden;

        chart.update();

        if (chart_name === 'space') {
            setSpaceHiddenSegments((prev) => {
                const newHiddenSegments = prev.includes(index)
                    ? prev.filter(i => i !== index)
                    : [...prev, index];
                return newHiddenSegments;
            });
        } else if (chart_name === 'user') {
            setUserHiddenSegments((prev) => {
                const newHiddenSegments = prev.includes(index)
                    ? prev.filter(i => i !== index)
                    : [...prev, index];
                return newHiddenSegments;
            });
        }
    };

    useEffect(()=>{
        if (state?.current_region) {
            setUserData((prev) => {
                const top_users = state.current_region?.top_usage?.users|| [];
                const unit = top_users?.reduce((max, user) => Math.max(max, user?.usage || 0), 0) > 1e12 ? 'TB' : 'GB';

                return {
                    ...prev,
                    labels: state.current_region.top_users?.map((user) => user?.name || "--"),
                    datasets: [{
                        ...prev.datasets[0],
                        data: top_users.map((user) => bytesToTB(user.usage, unit)),
                    }],
                    unit
                }
            });

            setSpaceData((prev) => {
                const top_spaces = state.current_region?.top_usage?.spaces || [];
                const unit = top_spaces?.reduce((max, space) => Math.max(max, space?.quota?.used || 0), 0) > 1e12 ? 'TB' : 'GB';

                return {
                    ...prev,
                    labels: state.current_region.top_spaces?.map((space) => space?.name || "--"),
                    datasets: [{
                        ...prev.datasets[0],
                        data: top_spaces.map((space) => bytesToTB(space?.quota?.used, unit))
                    }],
                    unit
                }
            });
        }
    }, [state?.current_region?.region]);

    return (
        <div className="content-grid__row content-grid__row--chart">
            <div className="chart-grid">
                {!state?.current_region?.top_usage?.spaces && <CloudSpaceEmptyChartComponent title="@Usages by Space@" />}
                {state?.current_region?.top_usage?.spaces && <div className="chart-grid__column">
                    <div className="chart-grid__column__title">{t('@Usages by Space@')}</div>
                    <div className="chart-grid__column__canvas">
                        <Doughnut data={spaceData} options={options} ref={spaceChartRef}/>
                    </div>
                    <div id="legend-left" className="chart-grid__column__legend">
                        <div className="legend-title">{t('@Top 5 Spaces:@')}</div>
                        <ul>
                            {
                                state?.current_region?.top_spaces && Array.from({length: 5},
                                    (_,i) => state?.current_region?.top_spaces?.slice(0,5)[i] ?? null
                                ).map((space, i) => (
                                    <li key={space?.id || `empty-${i}`} onClick={() => handleLegendClick(i, spaceChartRef, 'space')}>
                                        <span className="legend-color-box"
                                              style={{
                                                  backgroundColor: `${!space?.name ? '#e8e8e8' : initialData.datasets[0].backgroundColor[i]}`,
                                                  opacity: spaceHiddenSegments.includes(i) ? 0.5 : 1
                                        }}></span>
                                        <p className={spaceHiddenSegments.includes(i) ? 'del' : ''}>
                                            <span className="target-name">{space?.name || '--'}</span>
                                            <span className="usage-hilight">{space?.quota?.used ? `${bytesToTB(space?.quota?.used, spaceData.unit)}${spaceData.unit}` : ''}</span>
                                        </p>
                                    </li>
                                ))
                            }
                        </ul>
                    </div>
                </div>}
                <div className="chart-grid-divider"></div>
                {!state?.current_region?.top_usage?.users && <CloudSpaceEmptyChartComponent title="@Usages by Member@" />}
                {state?.current_region?.top_usage?.users && <div className="chart-grid__column">
                    <div className="chart-grid__column__title">{t('@Usages by Member@')}</div>
                    <div className="chart-grid__column__canvas">
                        <Doughnut data={userData} options={options} ref={userChartRef}/>
                    </div>
                    <div id="legend-right" className="chart-grid__column__legend">
                        <div className="legend-title">{t('@Top 5 Users:@')}</div>
                        <ul>
                            {
                                state?.current_region?.top_users && Array.from({length: 5},
                                    (_,i) => state?.current_region?.top_users?.slice(0,5)[i] ?? null
                                ).map((user, i) => (
                                    <li key={user?.id || `empty-${i}`} onClick={() => handleLegendClick(i, userChartRef, 'user')}>
                                        <span className="legend-color-box"
                                              style={{
                                                  backgroundColor: `${!user?.name ? '#e8e8e8' : initialData.datasets[0].backgroundColor[i]}`,
                                                  opacity: userHiddenSegments.includes(i) ? 0.5 : 1
                                        }}></span>
                                        <p className={userHiddenSegments.includes(i) ? 'del' : ''}>
                                            <span className="target-name">{user?.name || '--'}</span>
                                            <span className="usage-hilight">{user?.usage ? `${bytesToTB(user?.usage, userData.unit)}${userData.unit}` : ''}</span>
                                        </p>
                                    </li>
                                ))
                            }
                            { data && data.length > 0 &&
                                <li>
                                    <div className="legend-actions">
                                        <a href="#" onClick={e => {e.preventDefault(); setShowRemovedUserModal(true)}}>{t('@Deleted members space usage@')}</a>
                                    </div>
                                </li>
                            }
                        </ul>
                    </div>
                </div>}
            </div>
            {
                showRemovedUserModal && <CloudSpaceRemovedUserUsageModal isOpen={showRemovedUserModal} onClose={()=>setShowRemovedUserModal(false)} user_data={data}/>
            }
        </div>

    )
}

export default CloudSpaceStatsChartComponent;